import { useEffect, useMemo, useState } from 'react';
import { Download, Filter, Loader2, RefreshCw, Warehouse as WarehouseIcon } from 'lucide-react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toNumber = (value) => Number(value || 0);

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export default function StockRegisterPage() {
  const [activeTab, setActiveTab] = useState('finished');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [variants, setVariants] = useState([]);
  const [rawItems, setRawItems] = useState([]);

  const [filters, setFilters] = useState({
    warehouse_id: 'all',
    movement_type: 'all',
    variant_id: 'all',
    raw_item_id: 'all',
    date_from: '',
    date_to: '',
    search: '',
  });

  const activeWarehouseOptions = useMemo(() => (warehouses || []).filter((w) => w.is_active !== false), [warehouses]);
  const activeVariantOptions = useMemo(() => (variants || []).filter((v) => v.is_active !== false), [variants]);
  const activeRawItemOptions = useMemo(() => (rawItems || []).filter((r) => r.is_active !== false), [rawItems]);

  const loadMeta = async () => {
    const [warehouseRes, variantRes, rawItemRes] = await Promise.all([
      adminApi.getWarehouses(),
      adminApi.getProductVariants({ is_active: true }),
      adminApi.getRawItems(),
    ]);
    setWarehouses(warehouseRes.data || []);
    setVariants(variantRes.data || []);
    setRawItems(rawItemRes.data || []);
  };

  const buildParams = () => {
    const params = {};
    if (filters.warehouse_id !== 'all') params.warehouse_id = filters.warehouse_id;
    if (filters.movement_type !== 'all') params.movement_type = filters.movement_type;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (activeTab === 'finished' && filters.variant_id !== 'all') params.variant_id = filters.variant_id;
    if (activeTab === 'raw' && filters.raw_item_id !== 'all') params.raw_item_id = filters.raw_item_id;
    return params;
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const response = activeTab === 'finished'
        ? await adminApi.getStockMovements(params)
        : await adminApi.getRawStockMovements(params);
      setRows(response.data || []);
    } catch (error) {
      toast.error('Failed to load stock register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadMeta();
        await loadRows();
      } catch (error) {
        toast.error('Failed to load stock register data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    loadRows();
  }, [activeTab, filters.warehouse_id, filters.movement_type, filters.variant_id, filters.raw_item_id, filters.date_from, filters.date_to]);

  const movementTypeOptions = useMemo(() => {
    const values = new Set((rows || []).map((r) => r.type).filter(Boolean));
    const ordered = ['sale_issue', 'transfer_out', 'transfer_in', 'adjustment', 'receive', 'consume'];
    ordered.forEach((type) => values.add(type));
    return Array.from(values);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = (filters.search || '').trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.warehouse_name,
        row.sku,
        row.master_name,
        row.pack_size_label,
        row.raw_item_name,
        row.type,
        row.ref_type,
        row.ref_id,
        row.note,
        row.created_by_name,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, filters.search]);

  const stats = useMemo(() => {
    if (activeTab === 'finished') {
      const inbound = filteredRows.filter((r) => toNumber(r.qty) > 0).reduce((sum, r) => sum + toNumber(r.qty), 0);
      const outbound = filteredRows.filter((r) => toNumber(r.qty) < 0).reduce((sum, r) => sum + Math.abs(toNumber(r.qty)), 0);
      return {
        entries: filteredRows.length,
        inbound,
        outbound,
        net: inbound - outbound,
      };
    }

    const inboundBags = filteredRows.filter((r) => ['receive', 'transfer_in'].includes((r.type || '').toLowerCase()))
      .reduce((sum, r) => sum + toNumber(r.bags), 0);
    const outboundBags = filteredRows.filter((r) => ['consume', 'transfer_out'].includes((r.type || '').toLowerCase()))
      .reduce((sum, r) => sum + toNumber(r.bags), 0);

    return {
      entries: filteredRows.length,
      inbound: inboundBags,
      outbound: outboundBags,
      net: inboundBags - outboundBags,
    };
  }, [filteredRows, activeTab]);

  const downloadCsv = () => {
    const header = activeTab === 'finished'
      ? ['Date/Time', 'Warehouse', 'SKU', 'Product', 'Pack', 'Type', 'Qty', 'Direction', 'Ref Type', 'Ref ID', 'Batch', 'Note', 'User']
      : ['Date/Time', 'Warehouse', 'Raw Item', 'Type', 'Bags', 'Kg', 'Ref ID', 'Stock Ref No', 'Note', 'User'];

    const lines = [header.join(',')];

    filteredRows.forEach((row) => {
      if (activeTab === 'finished') {
        const qty = toNumber(row.qty);
        const direction = qty > 0 ? 'IN' : qty < 0 ? 'OUT' : 'NA';
        lines.push([
          csvEscape(formatDateTime(row.created_at)),
          csvEscape(row.warehouse_name),
          csvEscape(row.sku),
          csvEscape(row.master_name),
          csvEscape(row.pack_size_label),
          csvEscape(row.type),
          csvEscape(qty),
          csvEscape(direction),
          csvEscape(row.ref_type),
          csvEscape(row.ref_id),
          csvEscape(row.batch_no),
          csvEscape(row.note),
          csvEscape(row.created_by_name),
        ].join(','));
      } else {
        lines.push([
          csvEscape(formatDateTime(row.created_at)),
          csvEscape(row.warehouse_name),
          csvEscape(row.raw_item_name),
          csvEscape(row.type),
          csvEscape(row.bags),
          csvEscape(row.kg),
          csvEscape(row.ref_id),
          csvEscape(row.stock_ref_no),
          csvEscape(row.note),
          csvEscape(row.created_by_name),
        ].join(','));
      }
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stock-register-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadExcel = async () => {
    try {
      const dataset = activeTab === 'finished' ? 'stock-register-finished' : 'stock-register-raw';
      const res = await adminApi.downloadAdminDatasetExcel(dataset, buildParams());
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  const clearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      warehouse_id: 'all',
      movement_type: 'all',
      variant_id: 'all',
      raw_item_id: 'all',
      date_from: '',
      date_to: '',
      search: '',
    }));
  };

  return (
    <div className="space-y-6" data-testid="stock-register-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Stock Register</h1>
          <p className="text-[#5C4033] mt-1">Complete in/out movement log with filters, audit fields, and export.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadRows}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={downloadExcel}>
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          <Button onClick={downloadCsv} className="bg-[#C97A5E] hover:bg-[#B06850] text-white">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#8A6E60]">Entries</p><p className="text-2xl font-bold text-[#2D2420]">{stats.entries}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#8A6E60]">Inbound</p><p className="text-2xl font-bold text-emerald-700">{stats.inbound}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#8A6E60]">Outbound</p><p className="text-2xl font-bold text-rose-700">{stats.outbound}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#8A6E60]">Net</p><p className={cn('text-2xl font-bold', stats.net >= 0 ? 'text-emerald-700' : 'text-rose-700')}>{stats.net}</p></CardContent></Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="pb-3">
          <CardTitle className="text-[#2D2420] flex items-center"><Filter className="w-4 h-4 mr-2" /> Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="finished">Finished Goods</TabsTrigger>
              <TabsTrigger value="raw">Raw Stock</TabsTrigger>
            </TabsList>

            <TabsContent value="finished" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <Select value={filters.warehouse_id} onValueChange={(value) => setFilters((s) => ({ ...s, warehouse_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Warehouse" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {activeWarehouseOptions.map((wh) => <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.movement_type} onValueChange={(value) => setFilters((s) => ({ ...s, movement_type: value }))}>
                  <SelectTrigger><SelectValue placeholder="Movement Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {movementTypeOptions.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.variant_id} onValueChange={(value) => setFilters((s) => ({ ...s, variant_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Variant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Variants</SelectItem>
                    {activeVariantOptions.map((v) => <SelectItem key={v.id} value={v.id}>{v.sku} • {v.master_name} ({v.pack_size_label})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters((s) => ({ ...s, date_from: e.target.value }))} />
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters((s) => ({ ...s, date_to: e.target.value }))} />
                <Input placeholder="Search SKU, ref, note..." value={filters.search} onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))} />
              </div>
            </TabsContent>

            <TabsContent value="raw" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <Select value={filters.warehouse_id} onValueChange={(value) => setFilters((s) => ({ ...s, warehouse_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Warehouse" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {activeWarehouseOptions.map((wh) => <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.movement_type} onValueChange={(value) => setFilters((s) => ({ ...s, movement_type: value }))}>
                  <SelectTrigger><SelectValue placeholder="Movement Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {movementTypeOptions.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.raw_item_id} onValueChange={(value) => setFilters((s) => ({ ...s, raw_item_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Raw Item" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Raw Items</SelectItem>
                    {activeRawItemOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters((s) => ({ ...s, date_from: e.target.value }))} />
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters((s) => ({ ...s, date_to: e.target.value }))} />
                <Input placeholder="Search item, ref, note..." value={filters.search} onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))} />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end">
            <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-[#A8A8A8]">
              <WarehouseIcon className="w-10 h-10 mb-3" />
              <p>No movement entries found for selected filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {activeTab === 'finished' ? (
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date/Time</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">SKU</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Qty</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reference</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Note</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {filteredRows.map((row) => {
                      const qty = toNumber(row.qty);
                      const isIn = qty > 0;
                      return (
                        <tr key={row.id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4 text-sm text-[#2D2420]">{formatDateTime(row.created_at)}</td>
                          <td className="py-3 px-4 text-sm text-[#2D2420]">{row.warehouse_name || '-'}</td>
                          <td className="py-3 px-4 text-sm text-[#2D2420] font-medium">{row.sku || '-'}</td>
                          <td className="py-3 px-4 text-sm text-[#2D2420]">{row.master_name || '-'} {row.pack_size_label ? `(${row.pack_size_label})` : ''}</td>
                          <td className="py-3 px-4 text-sm">
                            <Badge className="bg-[#F3E9E4] text-[#7B4D37]">{row.type || '-'}</Badge>
                          </td>
                          <td className={cn('py-3 px-4 text-sm text-right font-semibold', isIn ? 'text-emerald-700' : 'text-rose-700')}>
                            {isIn ? '+' : ''}{qty}
                          </td>
                          <td className="py-3 px-4 text-xs text-[#5C4033]">{row.ref_type || '-'} {row.ref_id ? `• ${row.ref_id}` : ''}</td>
                          <td className="py-3 px-4 text-xs text-[#6B6B6B] max-w-[260px] truncate" title={row.note || ''}>{row.note || '-'}</td>
                          <td className="py-3 px-4 text-xs text-[#6B6B6B]">{row.created_by_name || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date/Time</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Bags</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Kg</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reference</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Note</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {filteredRows.map((row) => (
                      <tr key={row.id} className="hover:bg-[#FAFAF9]/60">
                        <td className="py-3 px-4 text-sm text-[#2D2420]">{formatDateTime(row.created_at)}</td>
                        <td className="py-3 px-4 text-sm text-[#2D2420]">{row.warehouse_name || '-'}</td>
                        <td className="py-3 px-4 text-sm text-[#2D2420] font-medium">{row.raw_item_name || '-'}</td>
                        <td className="py-3 px-4 text-sm"><Badge className="bg-[#F3E9E4] text-[#7B4D37]">{row.type || '-'}</Badge></td>
                        <td className="py-3 px-4 text-sm text-right text-[#2D2420]">{row.bags ?? '-'}</td>
                        <td className="py-3 px-4 text-sm text-right text-[#2D2420]">{row.kg ?? '-'}</td>
                        <td className="py-3 px-4 text-xs text-[#5C4033]">{row.ref_id || row.stock_ref_no || '-'}</td>
                        <td className="py-3 px-4 text-xs text-[#6B6B6B] max-w-[260px] truncate" title={row.note || ''}>{row.note || '-'}</td>
                        <td className="py-3 px-4 text-xs text-[#6B6B6B]">{row.created_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
